/**
 * Phase 7.UI.15k — pending CopeCart checkout intent helpers.
 *
 * When a logged-out visitor clicks a paid plan we stash the selected
 * checkout intent in sessionStorage before sending them to signup/login.
 * After successful authentication the post-login route reads the intent
 * back, calls the authenticated CopeCart checkout URL endpoint, and
 * redirects to the appropriate CopeCart page.
 *
 * sessionStorage is the right scope here:
 *   - survives the in-tab redirect through /signup or /login
 *   - does NOT leak across tabs or browser sessions
 *   - cleared automatically when the tab is closed (intent is single-use anyway)
 *
 * The intent is intentionally inert: it carries no auth material and
 * cannot be used to upgrade any user — it just stores the plan/interval
 * the user *wanted* to buy so we can recover gracefully after auth.
 */
const STORAGE_KEY = "stockaily_pending_checkout_intent";
const INTENT_TTL_MS = 30 * 60 * 1000; // 30 minutes — comfortably covers a signup flow

function _safeStorage() {
  // Phase 7.UI.15m — sessionStorage (tab-scoped) per product decision.
  // Rationale: a checkout intent should only live for the active
  // signup/login flow in the current tab. Cross-tab or cross-session
  // persistence is undesirable because:
  //   - the intent is single-use (consumed exactly once on success)
  //   - it must never silently surface on an unrelated future visit
  //   - the user might change their mind between sessions
  // sessionStorage survives the in-tab redirect through /signup or
  // /login (same browsing context), which is the only durability we
  // actually need. The 30-minute TTL below still bounds staleness
  // inside the same tab.
  try {
    if (typeof window === "undefined") return null;
    return window.sessionStorage;
  } catch {
    return null;
  }
}

/**
 * Save the user's intended paid checkout BEFORE we send them to auth.
 *
 * @param {Object} intent
 * @param {"pro"|"premium"} intent.plan
 * @param {"monthly"|"yearly"} intent.interval
 * @param {"en"|"de"|string} [intent.locale]
 * @param {string} [intent.affiliate_id]
 * @param {string} [intent.return_path]
 * @param {"pricing"|"billing"|"watchlist"|"upgrade_cta"|string} [intent.source]
 */
export function stashCheckoutIntent(intent) {
  const s = _safeStorage();
  if (!s || !intent) return;
  const payload = {
    plan: intent.plan,
    interval: intent.interval || "monthly",
    locale: intent.locale || null,
    affiliate_id: intent.affiliate_id || null,
    return_path: intent.return_path || null,
    source: intent.source || "unknown",
    created_at: new Date().toISOString(),
  };
  try {
    s.setItem(STORAGE_KEY, JSON.stringify(payload));
  } catch {
    /* ignore — sessionStorage quota / disabled */
  }
}

/**
 * Read the pending intent without consuming it. Returns null if missing,
 * malformed, or expired. Expired entries are also wiped from storage.
 */
export function peekCheckoutIntent() {
  const s = _safeStorage();
  if (!s) return null;
  let raw;
  try {
    raw = s.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
  if (!raw) return null;
  let intent;
  try {
    intent = JSON.parse(raw);
  } catch {
    try { s.removeItem(STORAGE_KEY); } catch { /* ignore */ }
    return null;
  }
  if (!intent || typeof intent !== "object") return null;
  if (!intent.plan || !["pro", "premium"].includes(intent.plan)) return null;
  if (!intent.interval || !["monthly", "yearly"].includes(intent.interval)) return null;
  // Expiry guard — defensive against stale tabs.
  const createdAt = intent.created_at ? Date.parse(intent.created_at) : 0;
  if (createdAt && (Date.now() - createdAt) > INTENT_TTL_MS) {
    try { s.removeItem(STORAGE_KEY); } catch { /* ignore */ }
    return null;
  }
  return intent;
}

/**
 * Atomically read AND remove the pending intent. Used by the post-login
 * continue page so a refresh after the redirect cannot double-spend the
 * intent and accidentally open CopeCart twice.
 */
export function consumeCheckoutIntent() {
  const s = _safeStorage();
  const intent = peekCheckoutIntent();
  if (s) {
    try { s.removeItem(STORAGE_KEY); } catch { /* ignore */ }
  }
  return intent;
}

export function clearCheckoutIntent() {
  const s = _safeStorage();
  if (s) {
    try { s.removeItem(STORAGE_KEY); } catch { /* ignore */ }
  }
}
